import { formatRelativeAge } from '../../lib/format';
import { domainOf } from '../../lib/url';
import type {
  FeedSourceDiscovery,
  FeedSourceKind,
  FeedSourceRead,
  FeedSourceStatus,
} from '../../types/api';

/**
 * The words the news surface uses about a followed source.
 *
 * One module rather than a copy per list: the Following list, a curation run's
 * receipt and the categories board all name the same rows, and a row must not
 * be called two different things depending on which of the three the user
 * happens to be looking at.
 */

/** Status vocabulary, worded as a reader would ask about it. */
export const FEED_STATUS_LABEL: Readonly<Record<FeedSourceStatus, string>> = {
  active: 'Active',
  demoted: 'Quiet',
  dead: 'Not working',
};

/**
 * Where the row came from, said as the user would say it. Four ways in, four
 * labels: the industry sweep, the paste field, our curated list, and the
 * assistant that went looking when the user described what they wanted.
 */
export const FEED_DISCOVERY_LABEL: Readonly<Record<FeedSourceDiscovery, string>> = {
  auto: 'Found for you',
  manual: 'Added by you',
  catalog: 'From our list',
  agent: 'Picked by AI assistant',
};

/**
 * How the row is read. Two words, because they are the honest difference: a
 * `feed` is a syndication feed the publisher maintains, a `page` is a page we
 * scrape because there is none. It prefixes the address rather than standing
 * alone as a badge — "Page https://…" is the whole fact in one line.
 */
export const FEED_KIND_LABEL: Readonly<Record<FeedSourceKind, string>> = {
  feed: 'Feed',
  page: 'Page',
};

/** Name of a source: its own title, else the domain, else the raw URL. */
export function sourceLabel(item: FeedSourceRead): string {
  return item.title?.trim() || domainOf(item.feed_url) || item.feed_url;
}

/**
 * The address actually being read, minus the scheme and a trailing slash.
 *
 * The full `feed_url` rather than its domain: two rows on the same site are
 * told apart by the path, and this is the line that answers "what exactly is
 * being fetched". The scheme is dropped from the *text* only — the link itself
 * still carries the whole URL.
 */
export function sourceAddress(item: FeedSourceRead): string {
  const trimmed = item.feed_url.replace(/^https?:\/\//i, '').replace(/\/+$/, '');
  return trimmed || item.feed_url;
}

/**
 * How fresh the source is: the age of its newest item, or that it dates
 * nothing. A feed publishing undated items is not a broken feed, so this is a
 * plain statement rather than a warning; the status badge beside it is where a
 * source stops being read.
 */
export function sourceAge(item: FeedSourceRead, now: Date = new Date()): string {
  return item.last_item_at === null ? 'no dated items' : formatRelativeAge(item.last_item_at, now);
}
